import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getTerminology } from '../api';
import { useAuth } from './AuthContext';
import { getCachedTerms, setCachedTerms } from '../utils/tenantStorage';
import { resolveTerms, makeT, navDisplayLabel } from '../utils/terminology';

const TerminologyContext = createContext(null);

const DEFAULT_VALUE = (() => {
  const terms = resolveTerms(null);
  const t = makeT(terms);
  return { terms, t, navLabel: (l) => navDisplayLabel(l, t), loaded: false, refresh: async () => {} };
})();

// Per-tenant words for Client / Affiliate / Department / Property. Seeds from
// the last cached map so an offline reload keeps the tenant's words; falls
// back to the defaults while loading, on error, or when logged out.
export function TerminologyProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const tenantSlug = user?.tenantSlug || undefined;
  const [terms, setTerms] = useState(() => resolveTerms(getCachedTerms(user?.tenantSlug || undefined)));
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!userId) {
      setTerms(resolveTerms(null));
      setLoaded(true);
      return;
    }
    try {
      const res = await getTerminology();
      const data = res.data && res.data.terminology;
      setTerms(resolveTerms(data));
      if (tenantSlug) setCachedTerms(data, tenantSlug);
    } catch (err) {
      // keep the current map (seeded from the cache) on a blip
    } finally {
      setLoaded(true);
    }
  }, [userId, tenantSlug]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => {
    const t = makeT(terms);
    return { terms, t, navLabel: (l) => navDisplayLabel(l, t), loaded, refresh };
  }, [terms, loaded, refresh]);

  return (
    <TerminologyContext.Provider value={value}>
      {children}
    </TerminologyContext.Provider>
  );
}

export function useTerms() {
  return useContext(TerminologyContext) || DEFAULT_VALUE;
}
